const PAGE_MARGIN_PT = 28;
const A4_WIDTH_PT = 595.28;
const A4_HEIGHT_PT = 841.89;
const PAGE_BG = '#f2f2f3';
/** Browser canvas dimension soft limit — stay under this after scale. */
const MAX_CANVAS_EDGE_PX = 8192;

export function dashboardPdfFilename(endDate?: string | null): string {
  const raw = (endDate ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return `dashboard-${raw}.pdf`;
  }
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `dashboard-${yyyy}-${mm}-${dd}.pdf`;
}

function waitNextFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

async function waitForFontsReady(): Promise<void> {
  if (!document.fonts) return;
  try {
    await document.fonts.ready;
    // Force-load body/UI font metrics before capture (avoids fallback-metric clipping).
    const bodyFont = getComputedStyle(document.body).fontFamily || 'sans-serif';
    const samples = [12, 13, 14, 16, 18, 20, 24, 30];
    await Promise.all(
      samples.map((size) => document.fonts.load(`${size}px ${bodyFont}`).catch(() => undefined))
    );
    await document.fonts.ready;
  } catch {
    // ignore font loading failures
  }
}

async function waitForFontsAndImages(root: HTMLElement): Promise<void> {
  await waitForFontsReady();
  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          const done = () => resolve();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        })
    )
  );
  await waitNextFrame();
  await new Promise((resolve) => setTimeout(resolve, 120));
  await waitNextFrame();
}

function findScrollMain(from: HTMLElement): HTMLElement | null {
  if (from.tagName === 'MAIN') return from;
  const nested = from.querySelector('main');
  if (nested instanceof HTMLElement) return nested;
  const closest = from.closest('main');
  return closest instanceof HTMLElement ? closest : null;
}

/** Resolve content column (TopBar + main) when available; else dashboard root. */
export function resolveDashboardExportRoot(dashboardRoot: HTMLElement): HTMLElement {
  const main = dashboardRoot.closest('main');
  const column = main?.parentElement;
  if (column instanceof HTMLElement && column.querySelector('header')) {
    return column;
  }
  return dashboardRoot;
}

function shouldIgnoreForPdf(el: Element): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return (
    el.hasAttribute('data-pdf-ignore') ||
    el.hasAttribute('data-html2canvas-ignore') ||
    el.getAttribute('data-html2canvas-ignore') === 'true'
  );
}

/**
 * Export-only stylesheet injected into the capture host.
 * Fixes html2canvas glyph clipping in fixed-height flex chips/buttons without
 * touching the live Dashboard CSS.
 */
function injectCaptureTextFixStyles(host: HTMLElement): void {
  const style = document.createElement('style');
  style.setAttribute('data-pdf-text-fix', 'true');
  style.textContent = `
    [data-pdf-capture-host],
    [data-pdf-capture-host] * {
      -webkit-font-smoothing: antialiased;
      text-rendering: geometricPrecision;
    }

    /* html2canvas often clips ascenders in tight fixed-height flex rows */
    [data-pdf-capture-host] header,
    [data-pdf-capture-host] header * {
      overflow: visible !important;
      overflow-x: visible !important;
      overflow-y: visible !important;
      text-overflow: clip !important;
    }

    [data-pdf-capture-host] button,
    [data-pdf-capture-host] input,
    [data-pdf-capture-host] select,
    [data-pdf-capture-host] textarea,
    [data-pdf-capture-host] [role="button"],
    [data-pdf-capture-host] header span,
    [data-pdf-capture-host] header label {
      line-height: 1.35 !important;
      overflow: visible !important;
      text-overflow: clip !important;
      white-space: nowrap;
    }

    [data-pdf-capture-host] button,
    [data-pdf-capture-host] [role="button"],
    [data-pdf-capture-host] header .inline-flex,
    [data-pdf-capture-host] header [class*="inline-flex"],
    [data-pdf-capture-host] header [class*="h-["] {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      box-sizing: border-box !important;
    }
  `;
  host.prepend(style);
}

function relaxFixedHeightControls(root: HTMLElement): void {
  const candidates = root.querySelectorAll<HTMLElement>(
    'header button, header input, header span, header label, header [role="button"], button, [role="button"]'
  );

  for (const el of candidates) {
    const computed = window.getComputedStyle(el);
    const height = Number.parseFloat(computed.height);
    if (!Number.isFinite(height) || height <= 0 || height > 64) continue;

    // Keep visual size close to the browser UI, but give html2canvas room for glyphs.
    const minHeight = Math.ceil(height + 4);
    el.style.setProperty('height', 'auto', 'important');
    el.style.setProperty('min-height', `${minHeight}px`, 'important');
    el.style.setProperty('max-height', 'none', 'important');
    el.style.setProperty('overflow', 'visible', 'important');
    el.style.setProperty('line-height', '1.35', 'important');
    el.style.setProperty('display', computed.display.includes('flex') ? computed.display : 'inline-flex', 'important');
    el.style.setProperty('align-items', 'center', 'important');

    const padY = Math.max(2, Number.parseFloat(computed.paddingTop) || 0);
    el.style.setProperty('padding-top', `${padY}px`, 'important');
    el.style.setProperty('padding-bottom', `${padY}px`, 'important');
  }
}

function unlockCloneTree(root: HTMLElement, widthPx: number): void {
  root.style.setProperty('width', `${Math.ceil(widthPx)}px`, 'important');
  root.style.setProperty('max-width', `${Math.ceil(widthPx)}px`, 'important');
  root.style.setProperty('height', 'auto', 'important');
  root.style.setProperty('max-height', 'none', 'important');
  root.style.setProperty('min-height', '0', 'important');
  root.style.setProperty('overflow', 'visible', 'important');
  root.style.setProperty('overflow-x', 'visible', 'important');
  root.style.setProperty('overflow-y', 'visible', 'important');
  root.style.setProperty('flex', '0 0 auto', 'important');
  root.style.setProperty('position', 'relative', 'important');
  root.style.setProperty('transform', 'none', 'important');
  root.style.setProperty('zoom', 'normal', 'important');
  root.style.setProperty('margin', '0', 'important');
  root.style.setProperty('left', 'auto', 'important');
  root.style.setProperty('right', 'auto', 'important');
  root.style.setProperty('top', 'auto', 'important');
  root.style.setProperty('bottom', 'auto', 'important');

  const mains = root.querySelectorAll('main');
  for (const main of mains) {
    if (!(main instanceof HTMLElement)) continue;
    main.style.setProperty('overflow', 'visible', 'important');
    main.style.setProperty('overflow-x', 'visible', 'important');
    main.style.setProperty('overflow-y', 'visible', 'important');
    main.style.setProperty('height', 'auto', 'important');
    main.style.setProperty('max-height', 'none', 'important');
    main.style.setProperty('flex', '0 0 auto', 'important');
    main.style.setProperty('min-height', '0', 'important');
  }

  // Flatten nested overflow clamps that survive cloneNode.
  for (const el of root.querySelectorAll<HTMLElement>('*')) {
    const style = window.getComputedStyle(el);
    if (
      style.overflow === 'hidden' ||
      style.overflow === 'clip' ||
      style.overflow === 'auto' ||
      style.overflow === 'scroll' ||
      style.overflowY === 'hidden' ||
      style.overflowY === 'clip' ||
      style.overflowY === 'auto' ||
      style.overflowY === 'scroll' ||
      style.overflowX === 'hidden' ||
      style.overflowX === 'clip' ||
      style.overflowX === 'auto' ||
      style.overflowX === 'scroll'
    ) {
      el.style.setProperty('overflow', 'visible', 'important');
      el.style.setProperty('overflow-x', 'visible', 'important');
      el.style.setProperty('overflow-y', 'visible', 'important');
    }
    if (style.maxHeight !== 'none') {
      el.style.setProperty('max-height', 'none', 'important');
    }
    if (style.transform && style.transform !== 'none') {
      // Avoid transform-induced fractional glyph clipping in the clone.
      if (el !== root) {
        el.style.setProperty('transform', 'none', 'important');
      }
    }
  }

  relaxFixedHeightControls(root);
}

/**
 * Clone the dashboard into a capture host so AppShell overflow/flex clamps
 * cannot crop the capture. Live DOM is left unchanged aside from scrollTop reset.
 */
function buildOffscreenCaptureClone(
  sourceRoot: HTMLElement,
  widthPx: number
): { host: HTMLElement; clone: HTMLElement; cleanup: () => void } {
  const host = document.createElement('div');
  host.setAttribute('data-pdf-capture-host', 'true');
  // Keep the host in the viewport (opacity 0) so font metrics match on-screen rendering.
  // Off-screen (-100000px) hosts can produce clipped glyph metrics in html2canvas.
  host.style.cssText = [
    'position:fixed',
    'left:0',
    'top:0',
    `width:${Math.ceil(widthPx)}px`,
    'height:auto',
    'overflow:visible',
    `background:${PAGE_BG}`,
    'pointer-events:none',
    'opacity:0',
    'z-index:2147483646',
  ].join(';');

  injectCaptureTextFixStyles(host);

  const clone = sourceRoot.cloneNode(true) as HTMLElement;
  unlockCloneTree(clone, widthPx);
  host.appendChild(clone);
  document.body.appendChild(host);

  return {
    host,
    clone,
    cleanup: () => {
      host.remove();
    },
  };
}

function measureCloneSize(clone: HTMLElement, widthPx: number): { width: number; height: number } {
  const height = Math.ceil(
    Math.max(clone.scrollHeight, clone.offsetHeight, clone.getBoundingClientRect().height, 1)
  );
  const width = Math.ceil(
    Math.max(widthPx, clone.scrollWidth, clone.offsetWidth, clone.getBoundingClientRect().width, 1)
  );
  return { width, height };
}

function pickCaptureScale(width: number, height: number): number {
  const maxDim = Math.max(width, height, 1);
  // Prefer integer-ish scale; keep quality without exploding canvas size.
  const raw = Math.min(2, MAX_CANVAS_EDGE_PX / maxDim);
  return Math.max(1, Math.floor(raw * 100) / 100);
}

function preferredBreakOffsetsPx(root: HTMLElement, scale: number): number[] {
  const rootRect = root.getBoundingClientRect();
  const candidates = root.querySelectorAll<HTMLElement>('[data-pdf-section]');
  const offsets: number[] = [];
  for (const el of candidates) {
    const rect = el.getBoundingClientRect();
    const top = (rect.top - rootRect.top) * scale;
    if (top > 8) offsets.push(top);
  }
  return Array.from(new Set(offsets.map((n) => Math.round(n)))).sort((a, b) => a - b);
}

function pickSliceEnd(
  startY: number,
  maxSliceHeight: number,
  canvasHeight: number,
  breaks: number[]
): number {
  const hardEnd = Math.min(startY + maxSliceHeight, canvasHeight);
  if (hardEnd >= canvasHeight) return canvasHeight;

  const snapThreshold = maxSliceHeight * 0.28;
  let best = hardEnd;
  for (const br of breaks) {
    if (br <= startY + 8) continue;
    if (br > hardEnd) break;
    if (hardEnd - br <= snapThreshold) {
      best = br;
    }
  }
  return best;
}

function canvasSliceToDataUrl(source: HTMLCanvasElement, y: number, sliceHeight: number): string {
  const pageCanvas = document.createElement('canvas');
  pageCanvas.width = source.width;
  pageCanvas.height = Math.max(1, sliceHeight);
  const ctx = pageCanvas.getContext('2d');
  if (!ctx) throw new Error('Could not create PDF page canvas');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.fillStyle = PAGE_BG;
  ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
  ctx.drawImage(source, 0, y, source.width, sliceHeight, 0, 0, source.width, sliceHeight);

  try {
    return pageCanvas.toDataURL('image/jpeg', 0.95);
  } catch {
    return pageCanvas.toDataURL('image/png');
  }
}

async function canvasToPdfPages(canvas: HTMLCanvasElement, breaks: number[]) {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'a4',
    compress: true,
  });

  const contentWidth = A4_WIDTH_PT - PAGE_MARGIN_PT * 2;
  const contentHeight = A4_HEIGHT_PT - PAGE_MARGIN_PT * 2;
  // Uniform scale from full canvas width — never stretch/crop horizontally.
  const pxPerPt = canvas.width / contentWidth;
  const maxSliceHeightPx = contentHeight * pxPerPt;

  let rendered = false;
  let y = 0;
  while (y < canvas.height - 1) {
    const sliceEnd = pickSliceEnd(y, maxSliceHeightPx, canvas.height, breaks);
    const sliceHeight = Math.max(1, sliceEnd - y);
    const imgData = canvasSliceToDataUrl(canvas, y, sliceHeight);
    const format = imgData.startsWith('data:image/png') ? 'PNG' : 'JPEG';
    const drawHeight = sliceHeight / pxPerPt;

    if (rendered) pdf.addPage();
    pdf.addImage(imgData, format, PAGE_MARGIN_PT, PAGE_MARGIN_PT, contentWidth, drawHeight);
    rendered = true;
    y = sliceEnd;
  }

  if (!rendered) {
    const imgData = canvasSliceToDataUrl(canvas, 0, Math.max(1, canvas.height));
    const format = imgData.startsWith('data:image/png') ? 'PNG' : 'JPEG';
    pdf.addImage(imgData, format, PAGE_MARGIN_PT, PAGE_MARGIN_PT, contentWidth, 1);
  }

  return pdf;
}

export type DashboardPdfExportOptions = {
  filename: string;
  waitForReady?: boolean;
};

async function captureClone(
  html2canvas: typeof import('html2canvas').default,
  clone: HTMLElement,
  width: number,
  height: number,
  scale: number
): Promise<HTMLCanvasElement> {
  const safeWidth = Math.ceil(width);
  const safeHeight = Math.ceil(height);
  clone.style.setProperty('height', `${safeHeight}px`, 'important');
  clone.style.setProperty('width', `${safeWidth}px`, 'important');

  return html2canvas(clone, {
    scale,
    useCORS: true,
    allowTaint: false,
    backgroundColor: PAGE_BG,
    logging: false,
    imageTimeout: 20000,
    width: safeWidth,
    height: safeHeight,
    windowWidth: safeWidth,
    windowHeight: safeHeight,
    x: 0,
    y: 0,
    scrollX: 0,
    scrollY: 0,
    foreignObjectRendering: false,
    ignoreElements: shouldIgnoreForPdf,
    onclone: (_doc, cloned) => {
      if (!(cloned instanceof HTMLElement)) return;
      // Re-apply text fidelity fixes inside html2canvas's own clone tree.
      unlockCloneTree(cloned, safeWidth);
      const host = cloned.closest('[data-pdf-capture-host]');
      if (host instanceof HTMLElement && !host.querySelector('[data-pdf-text-fix]')) {
        injectCaptureTextFixStyles(host);
      }
    },
  });
}

/**
 * Capture the full rendered dashboard (including below-the-fold content) and download a PDF.
 * Uses a temporary opacity-0 clone so AppShell overflow cannot crop the result.
 */
export async function downloadDashboardPdf(
  dashboardRoot: HTMLElement,
  options: DashboardPdfExportOptions
): Promise<void> {
  const html2canvas = (await import('html2canvas')).default;
  const preferredRoot = resolveDashboardExportRoot(dashboardRoot);
  const scrollParent = findScrollMain(preferredRoot);
  const prevScrollTop = scrollParent?.scrollTop ?? 0;

  // Always start capture from the top of the scroll container.
  if (scrollParent) {
    scrollParent.scrollTop = 0;
  }

  const widthPx = Math.ceil(
    Math.max(
      preferredRoot.clientWidth,
      preferredRoot.offsetWidth,
      preferredRoot.getBoundingClientRect().width,
      dashboardRoot.clientWidth,
      1
    )
  );

  let cleanupClone: (() => void) | undefined;

  try {
    if (options.waitForReady !== false) {
      await waitForFontsAndImages(preferredRoot);
    }

    const { clone, cleanup } = buildOffscreenCaptureClone(preferredRoot, widthPx);
    cleanupClone = cleanup;

    if (options.waitForReady !== false) {
      await waitForFontsAndImages(clone);
    } else {
      await waitNextFrame();
    }

    // One more paint after clone style fixes settle.
    await waitNextFrame();

    const { width, height } = measureCloneSize(clone, widthPx);
    if (height < 1 || width < 1) {
      throw new Error('Dashboard has no measurable content to export');
    }

    const scale = pickCaptureScale(width, height);
    const canvas = await captureClone(html2canvas, clone, width, height, scale);

    // If capture still looks viewport-sized while content is taller, retry once with pinned height.
    const expectedMinCanvasHeight = height * scale * 0.85;
    let finalCanvas = canvas;
    if (canvas.height < expectedMinCanvasHeight) {
      clone.style.setProperty('height', `${Math.ceil(height)}px`, 'important');
      await waitNextFrame();
      finalCanvas = await captureClone(html2canvas, clone, width, height, scale);
    }

    const breaks = preferredBreakOffsetsPx(clone, finalCanvas.height / Math.max(height, 1));
    const pdf = await canvasToPdfPages(finalCanvas, breaks);
    pdf.save(options.filename);
  } finally {
    cleanupClone?.();
    if (scrollParent) {
      scrollParent.scrollTop = prevScrollTop;
    }
  }
}
